/**
 * GPU 后端抽象（DESIGN.md §2.8）。
 *
 * 双后端策略：WebGPU 优先、WebGL2 回退。这一层是**薄**的——只抽象两者都能高效
 * 表达的概念（纹理、渲染目标、着色器程序、uniform、draw、readback）。
 * WebGPU 独有的 compute shader **不**放进这个接口：在 WebGL2 上用 transform
 * feedback 硬凑既丑又慢，按 §2.8 的决策，那类算法走 "WebGPU 加速 / WebGL2 退到
 * wasm+SIMD CPU" 的分叉，而不是强行统一。
 *
 * **本文件不得出现任何后端特有的类型**（`GPUDevice`、`WebGLRenderingContext`、
 * `navigator.gpu` …）。这不只是洁癖：一旦这些类型泄漏到公共接口，上层就会不知不觉
 * 依赖某个后端的语义，回退路径便形同虚设。M0-04 的验收标准里有一条 grep 断言，
 * test/gpu/layering.test.ts 会持续检查。
 *
 * 坐标与单位约定：
 * - 纹理尺寸单位是**像素**。
 * - 视口原点在**左上角**，y 轴向下（与文档坐标一致）。两个后端的实现负责各自
 *   抹平差异（WebGL 的 NDC y 轴向上），调用方不需要知道。
 */

/**
 * 纹理格式。
 *
 * `rgba16float` 是合成链路的主力：16 位半精度浮点能装下 HDR 与线性空间中间值，
 * 8 位在多层混合后会肉眼可见地断层。`rgba8unorm` 只用于最终输出与 UI 贴图。
 */
export type TextureFormat = 'rgba8unorm' | 'rgba16float' | 'r8unorm' | 'r16float';

/** 每种格式的每像素字节数。readback 时用它算缓冲大小。 */
export const BYTES_PER_PIXEL: Readonly<Record<TextureFormat, number>> = {
  rgba8unorm: 4,
  rgba16float: 8,
  r8unorm: 1,
  r16float: 2,
};

export interface TextureDescriptor {
  readonly width: number;
  readonly height: number;
  readonly format: TextureFormat;
  /** 是否需要作为渲染目标。为 false 时后端可以省掉 attachment 相关的分配。 */
  readonly renderTarget?: boolean;
  /** 调试标签，会透传给后端的 label 字段，出现在 GPU 调试器里。 */
  readonly label?: string;
}

/**
 * 纹理句柄。上层只持有它，不接触后端原生对象。
 *
 * 生命周期由调用方负责：`destroy()` 之后再使用会抛错，而不是静默访问已释放显存。
 */
export interface GpuTexture {
  readonly width: number;
  readonly height: number;
  readonly format: TextureFormat;
  readonly destroyed: boolean;
  destroy(): void;
}

/** uniform 允许的取值。纹理作为 uniform 传入即为采样器绑定。 */
export type UniformValue = number | readonly number[] | GpuTexture;

/**
 * 着色器源码。两份**必须**同时提供，否则换后端时才发现缺一份就晚了。
 *
 * 按 §2.8，两份应从同一模板生成而非手写重复（M1-11 会落实模板化）；
 * 在那之前，成对手写并由测试对拍数值结果。
 */
export interface ShaderSource {
  /** WebGPU 用。单个模块内需含 `vs_main` 与 `fs_main` 两个入口。 */
  readonly wgsl: string;
  /** WebGL2 用。GLSL ES 3.0，需自带 `#version 300 es`。 */
  readonly glsl: { readonly vertex: string; readonly fragment: string };
}

export interface ProgramDescriptor {
  readonly source: ShaderSource;
  /** 输出到的纹理格式，WebGPU 建管线时必须提前知道。 */
  readonly targetFormat: TextureFormat;
  readonly label?: string;
}

/** 已编译的着色器程序。 */
export interface GpuProgram {
  readonly destroyed: boolean;
  destroy(): void;
}

/** RGBA 四元组，取值 0–1 线性空间。 */
export type ClearColor = readonly [number, number, number, number];

export interface DrawDescriptor {
  readonly program: GpuProgram;
  /** 渲染目标。必须是以 `renderTarget: true` 创建的纹理。 */
  readonly target: GpuTexture;
  /** 顶点数。全屏三角形是 3。本层不做顶点缓冲抽象——合成器只画全屏图元。 */
  readonly vertexCount: number;
  readonly uniforms?: Readonly<Record<string, UniformValue>>;
  /** 提供则先清屏。不提供则保留目标原有内容。 */
  readonly clear?: ClearColor;
}

/**
 * 从纹理读回像素。
 *
 * 返回值按格式决定：8 位格式给 `Uint8Array`，16 位浮点格式给 `Float32Array`
 * （半精度在 JS 侧没有原生类型，统一升为 f32 便于断言与像素对拍）。
 */
export type ReadbackResult = Uint8Array | Float32Array;

/** 后端能力。上层据此决定降级路径，不要用 UA 或后端类型来推断。 */
export interface BackendCapabilities {
  /** 能否创建 `rgba16float` 的**渲染目标**（WebGL2 上依赖扩展，可能只有半精度或没有）。 */
  readonly renderToFloat16: boolean;
  /** 最大 2D 纹理边长（像素）。 */
  readonly maxTextureSize: number;
}

/** 批量绘制里的单次绘制。共享同一个渲染目标与同一次 render pass。 */
export interface BatchItem {
  readonly program: GpuProgram;
  readonly vertexCount: number;
  readonly uniforms?: Readonly<Record<string, UniformValue>>;
  /** 视口矩形 [x, y, w, h]，像素、原点左上。省略则覆盖整个目标。 */
  readonly viewport?: readonly [number, number, number, number];
}

export interface BatchDescriptor {
  readonly target: GpuTexture;
  readonly items: readonly BatchItem[];
  readonly clear?: ClearColor;
}

/** GPU 后端。WebGPU 与 WebGL2 实现同一套接口，同一份测试跑两遍（M0-05）。 */
export interface GpuBackend {
  readonly kind: 'webgpu' | 'webgl2';
  readonly capabilities: BackendCapabilities;
  createTexture(descriptor: TextureDescriptor): GpuTexture;
  /**
   * 把像素数据写入整张纹理。
   *
   * 目前只支持 8 位格式（`rgba8unorm` / `r8unorm`），`data` 长度必须等于
   * `width*height*每像素字节`。浮点格式的上传路径等 16bit 管线用到时再补——
   * 现在写一个没有任何调用方的 f16 转换只会成为未测试的死代码。
   *
   * 上下文丢失恢复后**内容不保证保留**：resource registry 只重建纹理本身，
   * 内容由调用方在恢复回调里重传。
   */
  writeTexture(texture: GpuTexture, data: Uint8Array): void;
  createProgram(descriptor: ProgramDescriptor): GpuProgram;
  draw(descriptor: DrawDescriptor): void;
  /**
   * 批量绘制：同一渲染目标上的多次绘制合并进**一次** render pass 与一次提交。
   *
   * 存在的理由是实测出来的：逐次 draw 时，创建 command encoder、开关 render pass、
   * 建 uniform buffer 与 bind group 的固定开销约 0.26 ms/次，而分块合成一帧要画
   * 成百上千个 tile——固定开销直接决定帧率，与实际像素工作量无关（M0-09 尖刺）。
   */
  drawBatch(descriptor: BatchDescriptor): void;
  /** 读回整张纹理。耗时操作，接受 `AbortSignal`（CLAUDE.md 代码约定）。 */
  readback(texture: GpuTexture, signal?: AbortSignal): Promise<ReadbackResult>;
  /** 释放后端及其全部资源。 */
  destroy(): void;
}

/** 纹理已释放后仍被使用时抛出。 */
export class DestroyedResourceError extends Error {
  constructor(what: string) {
    super(`${what} 已被 destroy()，不能继续使用。`);
    this.name = 'DestroyedResourceError';
  }
}

/** 校验纹理描述符。尺寸非法在创建时就报错，比在驱动里炸出天书好查。 */
export function validateTextureDescriptor(
  descriptor: TextureDescriptor,
  maxTextureSize: number,
): void {
  const { width, height, format } = descriptor;
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1) {
    throw new RangeError(`纹理尺寸必须是正整数，收到 ${width}×${height}。`);
  }
  if (width > maxTextureSize || height > maxTextureSize) {
    throw new RangeError(
      `纹理尺寸 ${width}×${height} 超出后端上限 ${maxTextureSize}。大图必须分块（见 DESIGN.md §4.3）。`,
    );
  }
  if (!(format in BYTES_PER_PIXEL)) {
    throw new RangeError(`未知纹理格式 ${format}。`);
  }
}
